import { useEffect, useRef, useState } from 'react';
import { MOCK } from '../lib/supabase.js';
import * as db from '../lib/data.js';
import { isStructuredMaterial } from '../lib/materials.js';

// วัสดุ preset (ตรงกับ deploy-19 seed) — ใช้ตอน MOCK ที่ไม่มี DB
const PRESET_MOCK = ['ผ้าโพลีเอสเตอร์', 'ผ้าโพลีคอตตอน', 'ผ้าฝ้าย', 'โพลีเอทีลีน', 'โพลีโพรพิลีน',
  'พลาสติก ABS', 'เรซิ่น', 'นีโอพรีน', 'ยาง', 'ซิลิโคน', 'ตาข่าย', 'ไนลอน', 'เหล็ก',
  'อลูมิเนียม', 'อลูมิเนียมอัลลอย', 'สแตนเลส', 'ไทเทเนียม', 'ไม้ไผ่'].map((name, i) => ({ id: 'm' + i, name }));

const parse = (v) => (v || '').split(',').map(s => s.trim()).filter(Boolean);
const join = (arr) => arr.join(', ');

// ช่องเลือกวัสดุ: เลือกจากคลัง (ติ๊กหลายอันได้) / พิมพ์เพิ่ม (จำไว้) / แก้-ลบ (กันพิมพ์ผิด)
// value = ข้อความวัสดุคั่นด้วย comma (เก็บใน product_classification.material เหมือนเดิม)
//
// ⚠ บั๊กที่ user เจอ 26 ต.ค. 69 "ใส่ material แล้วไม่จำ" — พิสูจน์ในฮาร์เนสแล้ว แก้ตรงนี้ 3 จุด:
//   ① เพิ่มวัสดุใหม่เดิม "รอเซิร์ฟเวอร์ก่อน" แล้วค่อยเขียนค่าที่จำไว้ตอนเริ่ม → ระหว่างรอ ถ้าเลือก/ลบวัสดุอื่น
//      ค่าเก่าเขียนทับกลับ → ใส่ชิปทันที (ไม่รอ) และคำนวณจากค่าล่าสุดเสมอ (latest ref)
//   ② พิมพ์ค้างไว้ในช่องแล้วไม่ได้กด Enter/เลือก → ดูเหมือนใส่แล้วแต่ไม่ได้ใส่ ตอนบันทึกหายเงียบ
//      → ถ้าตรงกับรายการเป๊ะ ใส่ให้เองตอนออกจากช่อง · ถ้าไม่ตรง แจ้งหน้าแก้ไขผ่าน onPendingChange ให้หยุดบันทึกแล้วบอก
//   ③ Enter ในช่อง "แก้ชื่อวัสดุ" ไม่ได้กัน → ไปกดบันทึกทั้งใบสินค้าแล้วหน้าต่างปิด
//
// ⚠ ค่าโครงสร้าง (isStructuredMaterial — มีส่วนประกอบ/เปอร์เซ็นต์ เช่น "ตัวเสื้อ: ฝ้าย 66%, โพลีเอสเตอร์ 34%; ริบ: ฝ้าย 95%")
//   ห้ามแตกเป็นชิป: จุลภาคอยู่ในส่วนเดียวกัน ถ้าแตกแล้วกด ✕ ส่วนประกอบเพี้ยนบนใบสำแดงโดยไม่มีใครเห็น → แก้เป็นข้อความทั้งก้อน
//   และห้าม addMaterial — ข้อความแบบนี้เป็นของสินค้าเดียว ไม่ใช่คำในรายการวัสดุที่ใช้ร่วมกันทุกใบ
//   textMode ล็อกไว้จนกว่าคนจะกดกลับเอง: ถ้าสลับตามค่าที่พิมพ์ ลบ % ตัวเดียวแล้วช่องกลายเป็นชิปกลางคัน เคอร์เซอร์หาย
//   (ห้ามสลับตอน blur ด้วย — กดบันทึกแล้วหน้าต่างขยับระหว่าง mousedown กับ click = click หลุด ดูคอมเมนต์ matpick-pending)
//   onModeChange(true/false) = ตอนนี้เป็นช่องข้อความไหม — คำแนะนำนอกช่องต้องตามโหมดจริง ไม่ใช่เดาจากค่า (textMode ล็อกค้างได้)
export default function MaterialPicker({ value, onChange, onPendingChange, onModeChange, inputId }) {
  const [vocab, setVocab] = useState(MOCK ? PRESET_MOCK : []);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [manage, setManage] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);   // แจ้งเบา ๆ เช่น จำวัสดุไว้ใช้ครั้งหน้าไม่สำเร็จ
  const [textLatched, setTextLatched] = useState(false);
  const structured = isStructuredMaterial(value);
  const textMode = textLatched || structured;
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const textRef = useRef(null);
  const focusText = useRef(false);   // เพิ่งกด Enter ใส่ข้อความหลายส่วน → ช่องชิปหายไป ย้ายเคอร์เซอร์ไปช่องข้อความ
  // ค่าล่าสุดสำหรับงานที่จบทีหลัง (หลัง await / listener ของ document) — กันเขียนทับด้วยค่าเก่า
  const latest = useRef({});
  latest.current = { value, vocab, query, onChange };

  const load = async () => {
    if (MOCK) return;
    try { setVocab(await db.listMaterials()); } catch { /* เงียบ — ยังเลือก/พิมพ์เองได้ */ }
  };
  useEffect(() => { load(); }, []);
  // ส่งคำที่พิมพ์ค้าง (ยังไม่ได้ใส่) ให้หน้าแก้ไขรู้ — ใช้กันบันทึกทั้งที่ยังมีคำค้าง
  // ⚠ ต้องส่ง "พร้อมกับ" การเปลี่ยนคำ (setQ) ห้ามส่งผ่าน useEffect:
  //   กดบันทึก → mousedown ใส่คำที่ตรงรายการให้ + ล้างช่อง → แต่ effect ยังไม่ทันรันก่อน click
  //   → หน้าแก้ไขยังเห็นคำค้างเดิม แล้วไม่ยอมบันทึก ทั้งที่ใส่ไปแล้ว (พิสูจน์ด้วยการดักอีเวนต์ในฮาร์เนส 26 ต.ค.)
  const setQ = (v) => { setQuery(v); onPendingChange?.(String(v ?? '').trim()); };
  useEffect(() => () => onPendingChange?.(''), []);
  useEffect(() => { if (structured) { setTextLatched(true); setQ(''); } }, [structured]);
  useEffect(() => { if (textMode && focusText.current) { focusText.current = false; textRef.current?.focus(); } }, [textMode]);
  useEffect(() => { onModeChange?.(textMode); }, [textMode]);

  // ทุกการเปลี่ยนวัสดุคำนวณจาก "ค่าล่าสุด" ไม่ใช่ค่าตอนกดปุ่ม
  const current = () => parse(latest.current.value);
  const hasIn = (arr, name) => arr.some(s => s.toLowerCase() === name.toLowerCase());
  const selected = parse(value);
  const has = (name) => hasIn(selected, name);
  const addSel = (name) => { const cur = current(); if (!hasIn(cur, name)) latest.current.onChange(join([...cur, name])); };
  const removeSel = (name) => latest.current.onChange(join(current().filter(s => s !== name)));
  const toggle = (name) => (hasIn(current(), name) ? removeSel(name) : addSel(name));

  // คำที่พิมพ์ตรงกับรายการเป๊ะ (ไม่สนตัวพิมพ์) → ใส่ให้เลยตอนออกจากช่อง · ไม่ตรง = ปล่อยค้างไว้ให้หน้าแก้ไขเตือน
  //   (ไม่เดาคำที่พิมพ์ไม่ครบ เช่น "ไนล" — วัสดุคือข้อมูลที่ broker ใช้ตีพิกัด ใส่คำผิดแย่กว่าเตือน)
  const commitExact = () => {
    const { query: qq, vocab: vv } = latest.current;
    const t = (qq || '').trim();
    if (!t) return;
    const m = vv.find(x => x.name.toLowerCase() === t.toLowerCase());
    if (m) { addSel(m.name); setQ(''); }
  };

  useEffect(() => {   // คลิกนอกกล่อง = ปิด (+ ใส่คำที่ตรงกับรายการให้)
    const h = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        commitExact();
        setOpen(false); setManage(false); setEditId(null);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const q = query.trim();
  const filtered = vocab.filter(m => !q || m.name.toLowerCase().includes(q.toLowerCase()));
  const exact = vocab.find(m => m.name.toLowerCase() === q.toLowerCase());

  const addNew = async () => {
    const name = q;
    if (!name) return;
    // ข้อความมีส่วนประกอบ/เปอร์เซ็นต์ → ใส่ในสินค้านี้ทั้งก้อน (คั่นส่วนด้วย ; ) ไม่จำลงรายการวัสดุ
    if (isStructuredMaterial(name)) {
      const cur = String(latest.current.value ?? '').trim();
      focusText.current = true;
      latest.current.onChange(cur ? `${cur}; ${name}` : name);
      setQ(''); setNote(null);
      return;
    }
    if (exact) { addSel(exact.name); setQ(''); return; }
    // ใส่ในสินค้านี้ทันที — ไม่รอเซิร์ฟเวอร์ (เดิมรอแล้วเขียนค่าเก่าทับ) · การ "จำไว้ใช้ครั้งหน้า" ทำเบื้องหลัง
    addSel(name); setQ(''); setNote(null);
    if (MOCK) { setVocab(v => [...v, { id: 'x' + Date.now(), name }]); return; }
    setBusy(true);
    try { await db.addMaterial(name); await load(); }
    catch { setNote(`ใส่ "${name}" ในสินค้านี้แล้ว แต่จำไว้ในรายการไม่สำเร็จ — ครั้งหน้าพิมพ์ใหม่ได้`); }
    finally { setBusy(false); }
  };
  const rename = async (m) => {
    const n = editText.trim(); if (!n) { setEditId(null); return; }
    setBusy(true);
    try {
      if (MOCK) setVocab(v => v.map(x => x.id === m.id ? { ...x, name: n } : x));
      else { await db.renameMaterial(m.id, n); await load(); }
      setEditId(null);
    } catch (e) { setNote(`แก้ชื่อวัสดุไม่สำเร็จ: ${e.message ?? String(e)}`); }
    finally { setBusy(false); }
  };
  const del = async (m) => {
    setBusy(true);
    try {
      if (MOCK) setVocab(v => v.filter(x => x.id !== m.id));
      else { await db.deleteMaterial(m.id); await load(); }
    } catch (e) { setNote(`ลบวัสดุไม่สำเร็จ: ${e.message ?? String(e)}`); }
    finally { setBusy(false); }
  };

  if (textMode) return (
    <div className="matpick" ref={wrapRef}>
      <textarea ref={textRef} id={inputId} className="matpick-text" rows={3} value={value ?? ''}
        onChange={e => latest.current.onChange(e.target.value)}
        placeholder="เช่น ตัวเสื้อ: ฝ้าย 100%; ซับใน: โพลีเอสเตอร์ 100%"/>
      <div className="fhint">
        ค่านี้มีหลายส่วนประกอบ จึงแก้เป็นข้อความทั้งก้อน — คั่นแต่ละส่วนด้วย ; ใบยื่นหน่วยงานจะแยกเป็นข้อให้เอง
        {!structured && <> · <button type="button" className="matpick-back" onClick={() => setTextLatched(false)}>กลับไปเลือกจากรายการ</button></>}
      </div>
    </div>
  );

  return (
    <div className="matpick" ref={wrapRef}>
      <div className="matpick-box" onClick={() => { setOpen(true); inputRef.current?.focus(); }}>
        {selected.map(s => (
          <span className="matchip" key={s}>{s}
            <button type="button" onClick={(e) => { e.stopPropagation(); removeSel(s); }} title="เอาออก">✕</button>
          </span>
        ))}
        <input ref={inputRef} id={inputId} value={query} onFocus={() => setOpen(true)}
          onChange={e => { setQ(e.target.value); setOpen(true); }}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addNew(); } }}
          // Tab ออกไปช่องอื่น (โฟกัสย้ายออกนอกกล่องนี้) → ใส่คำที่ตรงกับรายการให้
          onBlur={e => { if (e.relatedTarget && !wrapRef.current?.contains(e.relatedTarget)) { commitExact(); setOpen(false); } }}
          placeholder={selected.length ? 'เพิ่มวัสดุ…' : 'เลือก / พิมพ์วัสดุ…'}/>
      </div>
      {/* ช่องข้อความ "จองที่ไว้ตลอด" (สูงหนึ่งบรรทัดคงที่) — ห้ามเรนเดอร์แบบมี/ไม่มี:
          กดปุ่มบันทึก → mousedown ปิดรายการ/ใส่คำให้ → ข้อความนี้โผล่หรือหาย → ปุ่มขยับ 20px ระหว่างกดกับปล่อย
          → click หลุดเป้า กดบันทึกแล้วไม่เกิดอะไร (เจอในฮาร์เนส 26 ต.ค.) */}
      <div className="matpick-pending" aria-live="polite">
        {note ? note : q && !open ? (
          <>ยังไม่ได้ใส่ "{q}" — กด Enter หรือ <button type="button"
            onClick={() => { setOpen(true); inputRef.current?.focus(); }}>เลือกจากรายการ</button></>
        ) : null}
      </div>

      {open && (
        <div className="matpick-drop">
          {!manage ? (
            <>
              {q && !exact && (
                <button type="button" className="mat-add-new" disabled={busy} onClick={addNew}>
                  {isStructuredMaterial(q)
                    ? <>＋ ใส่ "{q}" ในสินค้านี้ (ข้อความหลายส่วน ไม่เก็บลงรายการ)</>
                    : <>＋ เพิ่ม "{q}" เป็นวัสดุใหม่ (จำไว้ใช้ครั้งหน้า)</>}
                </button>
              )}
              <div className="mat-opts">
                {filtered.map(m => (
                  <button type="button" key={m.id} className={`mat-opt ${has(m.name) ? 'on' : ''}`}
                    onClick={() => toggle(m.name)}>
                    <span className="mat-opt-nm">{m.name}</span>
                    {has(m.name) && <span className="mat-tick">✓</span>}
                  </button>
                ))}
                {!filtered.length && <div className="jr-empty">ไม่มีในรายการ — พิมพ์แล้วกด "เพิ่มเป็นวัสดุใหม่"</div>}
              </div>
              <button type="button" className="mat-manage-toggle" onClick={() => setManage(true)}>จัดการรายการวัสดุ (แก้ / ลบ) →</button>
            </>
          ) : (
            <>
              <div className="mat-manage-h">
                <span>แก้ / ลบ วัสดุ (กันพิมพ์ผิด)</span>
                <button type="button" onClick={() => { setManage(false); setEditId(null); }}>เสร็จ</button>
              </div>
              <div className="mat-opts">
                {vocab.map(m => (
                  <div className="mat-manage-row" key={m.id}>
                    {editId === m.id ? (
                      <>
                        <input className="mat-edit-in" value={editText} autoFocus
                          onChange={e => setEditText(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); rename(m); } }}/>
                        <button type="button" className="cardbtn" disabled={busy} onClick={() => rename(m)}>บันทึก</button>
                        <button type="button" className="cardbtn" onClick={() => setEditId(null)}>ยกเลิก</button>
                      </>
                    ) : (
                      <>
                        <span className="mat-manage-nm">{m.name}</span>
                        <button type="button" className="cardbtn" onClick={() => { setEditId(m.id); setEditText(m.name); }}>แก้</button>
                        <button type="button" className="cardbtn del" disabled={busy} onClick={() => del(m)}>ลบ</button>
                      </>
                    )}
                  </div>
                ))}
                {!vocab.length && <div className="jr-empty">ยังไม่มีวัสดุ</div>}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
